import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft, MapPin, Phone, Camera, Check, X, Loader2, Navigation,
  LogIn, LogOut, Wallet, CircleDollarSign, FileText, Send,
  Truck, Clock, CalendarDays, Route, ThumbsUp, ThumbsDown, CloudOff, Gauge,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { motApi, confirmarEntrega, acaoEntregador, flushOutbox, outboxCount } from '@/lib/motoristaApi'
import { formatMoney } from '@/lib/api'

const FORMAS = ['dinheiro', 'pix', 'cheque', 'boleto', 'prazo', 'cartao']

type Rec = { forma: string; valor: number; conta_id?: number; cheque_banco?: string; cheque_numero?: string; cheque_bom_para?: string }
// formas que precisam escolher o banco de destino (pra cair certo no acerto)
const PEDE_BANCO = ['pix', 'cartao']

// ---- estado otimista local (offline): sobrevive recarregar a página ----
const flag = (k: string) => { try { return localStorage.getItem(k) === '1' } catch { return false } }
const setFlag = (k: string) => { try { localStorage.setItem(k, '1') } catch { /* */ } }
const localDone = (cid: number): Record<number, string> => { try { return JSON.parse(localStorage.getItem('mot_done_' + cid) || '{}') } catch { return {} } }
const addLocalDone = (cid: number, pid: number, st: string) => { const d = localDone(cid); d[pid] = st; try { localStorage.setItem('mot_done_' + cid, JSON.stringify(d)) } catch { /* */ } }

function pegarGeo(): Promise<{ lat: number | null; lon: number | null }> {
  return new Promise((res) => {
    if (!navigator.geolocation) return res({ lat: null, lon: null })
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => res({ lat: null, lon: null }),
      { timeout: 6000, enableHighAccuracy: true },
    )
  })
}

export default function EntregadorCarga() {
  const { id } = useParams()
  const cid = Number(id)
  const nav = useNavigate()
  const qc = useQueryClient()
  const [parada, setParada] = useState<any | null>(null)
  const [busy, setBusy] = useState(false)
  // estado otimista (offline)
  const [aceiteLocal, setAceiteLocal] = useState(flag('mot_aceite_' + cid))
  const [checkinLocal, setCheckinLocal] = useState(flag('mot_checkin_' + cid))
  const [checkoutLocal, setCheckoutLocal] = useState(flag('mot_checkout_' + cid))
  const [done, setDone] = useState<Record<number, string>>(localDone(cid))
  const [pend, setPend] = useState(outboxCount())
  const [online, setOnline] = useState(navigator.onLine)
  const [odo, setOdo] = useState<null | 'saida' | 'chegada'>(null)  // modal do odômetro

  const { data, isLoading } = useQuery({
    queryKey: ['mot-carga', id],
    queryFn: async () => (await motApi.get(`/cargas/${id}`)).data,
    refetchInterval: 30000,
  })

  // sincroniza a fila offline e acompanha conexão/pendências
  useEffect(() => {
    const sync = async () => { await flushOutbox(); setPend(outboxCount()); qc.invalidateQueries({ queryKey: ['mot-carga', id] }) }
    sync()
    const on = () => { setOnline(true); sync() }
    const off = () => setOnline(false)
    window.addEventListener('online', on); window.addEventListener('offline', off)
    const t = setInterval(() => setPend(outboxCount()), 4000)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); clearInterval(t) }
  }, [id])

  async function checkin(km?: number) {
    setBusy(true)
    try {
      const g = await pegarGeo()
      const r = await acaoEntregador(`/cargas/${id}/checkin`, { ...g, km })
      if (r.online) { toast.success('Rota iniciada. Boa viagem! 🚚'); qc.invalidateQueries({ queryKey: ['mot-carga', id] }) }
      else { setFlag('mot_checkin_' + cid); setCheckinLocal(true); setPend(outboxCount()); toast('Sem internet: rota iniciada no aparelho. Sincroniza depois.') }
    } finally { setBusy(false) }
  }

  // Encerrar: avisa se há paradas pendentes, depois pede o km de chegada.
  function abrirCheckout() {
    const pendd = (data?.paradas || []).filter((p: any) => !p.entrega && !done[p.pedido_id]).length
    if (pendd > 0 && !confirm(`Ainda há ${pendd} parada(s) sem confirmar. Encerrar a rota mesmo assim?`)) return
    setOdo('chegada')
  }

  async function checkout(km?: number) {
    setBusy(true)
    try {
      const g = await pegarGeo()
      const r = await acaoEntregador(`/cargas/${id}/checkout`, { ...g, km })
      if (r.online) { toast.success('Rota encerrada. Caixa enviado pro acerto.'); qc.invalidateQueries({ queryKey: ['mot-carga', id] }); qc.invalidateQueries({ queryKey: ['mot-cargas'] }) }
      else { setFlag('mot_checkout_' + cid); setCheckoutLocal(true); setPend(outboxCount()); toast('Sem internet: rota encerrada no aparelho. Sincroniza depois.') }
    } finally { setBusy(false) }
  }

  if (isLoading || !data) return <div className="min-h-screen grid place-items-center text-ink-400">Carregando…</div>

  // Gate: antes de rodar, o motorista vê o romaneio (rota/dias/totais) e ACEITA (assina).
  if (!data.romaneio_aceito_em && !aceiteLocal) {
    return <RomaneioAceite
      cargaId={cid} recusado={!!data.romaneio_recusado_em}
      onBack={() => nav('/m/entregador/cargas')}
      onAceito={(offline: boolean) => {
        if (offline) { setFlag('mot_aceite_' + cid); setAceiteLocal(true); setPend(outboxCount()) }
        else qc.invalidateQueries({ queryKey: ['mot-carga', id] })
      }}
    />
  }

  const caixa: Record<string, number> = data.caixa || {}
  const efetiva = (p: any) => p.entrega || (done[p.pedido_id] ? { status: done[p.pedido_id], pendente: true } : null)
  const checkinFeito = !!data.checkin_em || checkinLocal
  const checkoutFeito = !!data.checkout_em || checkoutLocal
  const confirmadas = data.paradas.filter((p: any) => efetiva(p)).length

  return (
    <div className="min-h-screen bg-ink-50 pb-28">
      <header className="sticky top-0 z-10 bg-emerald-700 text-white px-2 h-14 flex items-center gap-2 shadow">
        <button onClick={() => nav('/m/entregador/cargas')} className="p-2 rounded-lg hover:bg-white/10"><ChevronLeft className="h-6 w-6" /></button>
        <div className="flex-1">
          <div className="font-bold leading-tight">Carga #{data.numero}</div>
          <div className="text-[11px] text-white/70 leading-tight">{confirmadas}/{data.paradas.length} entregas · {data.veiculo || 's/ veículo'}</div>
        </div>
        {(!online || pend > 0) && (
          <span className="flex items-center gap-1 text-[11px] font-bold bg-white/15 rounded-full px-2 py-1">
            <CloudOff className="h-4 w-4" />{pend > 0 ? `${pend} p/ enviar` : 'offline'}
          </span>
        )}
      </header>

      {/* Caixa da carga */}
      <div className="p-4 max-w-lg mx-auto">
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-4 shadow-lg">
          <div className="flex items-center gap-2 text-white/80 text-sm"><Wallet className="h-4 w-4" /> Caixa da carga</div>
          <div className="text-3xl font-extrabold mt-1">{formatMoney(data.total_recebido)}</div>
          <div className="text-xs text-white/70">de {formatMoney(data.total_previsto)} previsto</div>
          {Object.keys(caixa).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(caixa).map(([f, v]) => (
                <span key={f} className="text-[11px] bg-white/20 rounded-full px-2 py-1 capitalize">{f}: {formatMoney(v)}</span>
              ))}
            </div>
          )}
        </div>

        {/* Check-in / check-out */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button onClick={() => !checkinFeito && setOdo('saida')} disabled={busy || checkinFeito}
            className="h-14 rounded-2xl font-bold flex items-center justify-center gap-2 bg-white border-2 border-emerald-200 text-emerald-700 disabled:opacity-40 active:scale-95 transition">
            <LogIn className="h-5 w-5" />{checkinFeito ? 'Em rota ✓' : 'Iniciar rota'}
          </button>
          <button onClick={abrirCheckout} disabled={busy || checkoutFeito}
            className="h-14 rounded-2xl font-bold flex items-center justify-center gap-2 bg-violet-600 text-white disabled:opacity-40 active:scale-95 transition">
            <LogOut className="h-5 w-5" />{checkoutFeito ? 'Encerrada' : 'Encerrar rota'}
          </button>
        </div>
        {/* Km do odômetro capturado */}
        {(data.km_saida != null || data.km_chegada != null) && (
          <div className="flex items-center justify-center gap-4 mt-2 text-xs text-ink-500">
            {data.km_saida != null && <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" />Saída: <b>{data.km_saida} km</b></span>}
            {data.km_chegada != null && <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" />Chegada: <b>{data.km_chegada} km</b></span>}
            {data.km_saida != null && data.km_chegada != null && data.km_chegada >= data.km_saida && (
              <span className="text-emerald-600 font-bold">Rodou {(data.km_chegada - data.km_saida).toFixed(1)} km</span>
            )}
          </div>
        )}

        {/* Paradas */}
        <div className="mt-5 space-y-3">
          {data.paradas.map((p: any, i: number) => {
            const e = efetiva(p)
            const cor = !e ? 'border-ink-100' : e.status === 'entregue' ? 'border-emerald-300' : 'border-red-300'
            return (
              <button key={p.pedido_id} onClick={() => setParada(p)}
                className={`w-full text-left bg-white rounded-2xl shadow-sm border-2 ${cor} p-4 active:scale-[0.99] transition`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold grid place-items-center text-sm">{p.sequencia ?? i + 1}</span>
                    <div className="min-w-0">
                      <div className="font-bold text-ink-800 truncate">{p.cliente}</div>
                      <div className="text-xs text-ink-400 truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{p.endereco || 'Sem endereço'}</div>
                    </div>
                  </div>
                  {e
                    ? (e.status === 'entregue'
                      ? <span className="shrink-0 text-emerald-600 flex items-center gap-1 text-xs font-bold"><Check className="h-4 w-4" />Entregue{e.pendente ? ' ⟳' : ''}</span>
                      : <span className="shrink-0 text-red-600 flex items-center gap-1 text-xs font-bold"><X className="h-4 w-4" />Não{e.pendente ? ' ⟳' : ''}</span>)
                    : <span className="shrink-0 text-[11px] font-bold text-amber-600">Pendente</span>}
                </div>
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="text-ink-500 capitalize">{p.forma_pagamento}</span>
                  <span className="font-bold text-ink-800">{formatMoney(p.total)}</span>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {parada && (
        <ModalEntrega
          parada={parada} cargaId={cid}
          onClose={() => setParada(null)}
          onSaved={(pedidoId: number, status: string) => {
            setParada(null); addLocalDone(cid, pedidoId, status); setDone(localDone(cid))
            setPend(outboxCount()); qc.invalidateQueries({ queryKey: ['mot-carga', id] })
          }}
        />
      )}

      {odo && (
        <OdometroModal
          tipo={odo} busy={busy}
          minimo={odo === 'chegada' ? (data.km_saida ?? undefined) : undefined}
          onClose={() => setOdo(null)}
          onConfirma={async (km?: number) => {
            const fn = odo === 'saida' ? checkin : checkout
            setOdo(null)
            await fn(km)
          }}
        />
      )}
    </div>
  )
}

// Modal dead-simple p/ o motorista digitar o odômetro (km) no início e no fim da rota.
// Pode pular se não souber/quiser — o km vira opcional (auditoria usa o que tiver).
function OdometroModal({ tipo, minimo, busy, onClose, onConfirma }: any) {
  const [km, setKm] = useState('')
  const saida = tipo === 'saida'
  const n = parseFloat(km.replace(',', '.'))
  const invalido = km !== '' && (isNaN(n) || n <= 0 || (minimo != null && n < minimo))
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-3xl rounded-t-3xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-ink-800 font-extrabold text-lg">
          <Gauge className="h-6 w-6 text-violet-600" />
          {saida ? 'Km de saída' : 'Km de chegada'}
        </div>
        <p className="text-sm text-ink-500 mt-1">
          {saida
            ? 'Olhe o odômetro do veículo e digite o km marcado AGORA, antes de sair.'
            : 'Digite o km que está marcando no odômetro ao voltar.'}
        </p>
        <input
          autoFocus type="number" inputMode="decimal" value={km}
          onChange={(e) => setKm(e.target.value)}
          placeholder="Ex.: 124530"
          className="mt-4 w-full h-16 rounded-2xl border-2 border-ink-200 text-center text-3xl font-extrabold text-ink-800 focus:border-violet-500 outline-none"
        />
        {minimo != null && (
          <p className="text-[11px] text-ink-400 mt-1 text-center">Saída foi {minimo} km — a chegada deve ser maior.</p>
        )}
        {invalido && <p className="text-xs text-red-600 mt-1 text-center font-semibold">Valor inválido.</p>}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button onClick={() => onConfirma(undefined)} disabled={busy}
            className="h-14 rounded-2xl border-2 border-ink-200 text-ink-500 font-bold disabled:opacity-40 active:scale-95">
            Pular
          </button>
          <button onClick={() => onConfirma(km === '' ? undefined : n)} disabled={busy || invalido}
            className="h-14 rounded-2xl bg-violet-600 text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95">
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Check className="h-5 w-5" />{saida ? 'Iniciar rota' : 'Encerrar'}</>}
          </button>
        </div>
      </div>
    </div>
  )
}

function ModalEntrega({ parada, cargaId, onClose, onSaved }: any) {
  const e = parada.entrega
  const [entregue, setEntregue] = useState(e ? e.status === 'entregue' : true)
  const [recs, setRecs] = useState<Rec[]>(
    e?.recebimentos?.length ? e.recebimentos : [{ forma: parada.forma_pagamento || 'dinheiro', valor: parada.total }],
  )
  const [motivo, setMotivo] = useState(e?.motivo_nao_entrega || '')
  const [obs, setObs] = useState(e?.observacao || '')
  const [foto, setFoto] = useState<File | null>(null)
  const [fotoPreview, setFotoPreview] = useState<string | null>(e?.foto_url || null)
  const [saving, setSaving] = useState(false)
  const [gerandoNota, setGerandoNota] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const { data: bancos } = useQuery({
    queryKey: ['mot-bancos'],
    queryFn: async () => (await motApi.get('/bancos')).data as any[],
    staleTime: 5 * 60_000,
  })

  const totalRec = recs.reduce((s, r) => s + (Number(r.valor) || 0), 0)

  async function gerarNota() {
    if (!confirm(`Emitir nota fiscal do pedido #${parada.numero} e mandar pro cliente?`)) return
    setGerandoNota(true)
    try {
      const { data } = await motApi.post(`/pedidos/${parada.pedido_id}/gerar-nota`)
      const url = window.location.origin + data.danfe_url
      toast.success(`Nota ${data.numero} emitida ✓`)
      const tel = (data.telefone || '').replace(/\D/g, '')
      const txt = encodeURIComponent(`Olá! Segue a nota fiscal da sua compra (VELORE ERP): ${url}`)
      const wa = tel ? `https://wa.me/55${tel}?text=${txt}` : `https://wa.me/?text=${txt}`
      window.open(wa, '_blank')
    } catch { /* interceptor avisa */ } finally { setGerandoNota(false) }
  }

  function setRec(i: number, patch: Partial<Rec>) {
    setRecs((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  }

  function onFoto(ev: React.ChangeEvent<HTMLInputElement>) {
    const f = ev.target.files?.[0]
    if (f) { setFoto(f); setFotoPreview(URL.createObjectURL(f)) }
  }

  async function salvar() {
    if (entregue && totalRec <= 0 && !confirm('Nenhum valor recebido. Confirmar entrega assim mesmo?')) return
    if (!entregue && !motivo.trim()) { toast.error('Informe o motivo da não entrega.'); return }
    if (entregue && recs.some((r) => PEDE_BANCO.includes(r.forma) && !r.conta_id)) {
      toast.error('Escolha o banco que recebeu o PIX/cartão.'); return
    }
    setSaving(true)
    try {
      const g = await pegarGeo()
      const status = entregue ? 'entregue' : 'nao_entregue'
      const r = await confirmarEntrega({
        carga_id: cargaId, pedido_id: parada.pedido_id, status,
        recebimentos: entregue ? recs.map((x) => ({ ...x, valor: Number(x.valor) || 0 })) : [],
        motivo_nao_entrega: entregue ? null : motivo.trim(),
        observacao: obs.trim() || null, lat: g.lat, lon: g.lon,
      }, foto)
      if (r.online) toast.success(entregue ? 'Entrega confirmada ✓' : 'Não entrega registrada')
      else toast('Sem internet: salvo no aparelho. Sincroniza quando voltar o sinal. 📥')
      onSaved(parada.pedido_id, status)
    } catch { /* interceptor já avisa (erro do servidor) */ } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-ink-50 w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()}>
        <div className="sticky top-0 bg-white px-4 py-3 border-b flex items-center justify-between">
          <div className="min-w-0">
            <div className="font-extrabold text-ink-800 truncate">{parada.cliente}</div>
            <div className="text-xs text-ink-400">Pedido #{parada.numero} · {formatMoney(parada.total)}</div>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 text-ink-400"><X className="h-6 w-6" /></button>
        </div>

        <div className="p-4 space-y-4">
          {parada.maps_url && (
            <a href={parada.maps_url} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 text-blue-600 font-semibold text-sm">
              <Navigation className="h-4 w-4" />{parada.endereco}
            </a>
          )}
          {parada.telefone && (
            <a href={`tel:${parada.telefone}`} className="flex items-center gap-2 text-emerald-600 font-semibold text-sm">
              <Phone className="h-4 w-4" />{parada.telefone}
            </a>
          )}

          {/* Gerar nota fiscal e mandar no WhatsApp */}
          {parada.tipo_venda !== 'fiscal' ? (
            <button onClick={gerarNota} disabled={gerandoNota}
              className="w-full h-12 rounded-2xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition">
              {gerandoNota ? <Loader2 className="h-5 w-5 animate-spin" /> : <><FileText className="h-5 w-5" />Gerar nota e mandar no WhatsApp</>}
            </button>
          ) : (
            <button onClick={gerarNota} disabled={gerandoNota}
              className="w-full h-10 rounded-xl border border-blue-200 text-blue-600 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {gerandoNota ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Reenviar nota no WhatsApp</>}
            </button>
          )}

          {/* Toggle entregue / não */}
          <div className="grid grid-cols-2 gap-2 bg-white p-1 rounded-2xl border">
            <button onClick={() => setEntregue(true)}
              className={`h-12 rounded-xl font-bold flex items-center justify-center gap-2 ${entregue ? 'bg-emerald-600 text-white' : 'text-ink-500'}`}>
              <Check className="h-5 w-5" />Entregue
            </button>
            <button onClick={() => setEntregue(false)}
              className={`h-12 rounded-xl font-bold flex items-center justify-center gap-2 ${!entregue ? 'bg-red-600 text-white' : 'text-ink-500'}`}>
              <X className="h-5 w-5" />Não entregue
            </button>
          </div>

          {entregue ? (
            <div className="bg-white rounded-2xl border p-3 space-y-3">
              <div className="flex items-center gap-2 font-bold text-ink-700"><CircleDollarSign className="h-5 w-5 text-emerald-600" />Recebimento</div>
              {recs.map((r, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex gap-2">
                    <select value={r.forma} onChange={(ev) => setRec(i, { forma: ev.target.value })}
                      className="input h-11 flex-1 capitalize">
                      {FORMAS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <input type="number" inputMode="decimal" step="0.01" value={r.valor}
                      onChange={(ev) => setRec(i, { valor: parseFloat(ev.target.value) })}
                      className="input h-11 w-32 text-right font-bold" />
                    {recs.length > 1 && (
                      <button onClick={() => setRecs((rs) => rs.filter((_, idx) => idx !== i))}
                        className="px-3 text-red-500"><X className="h-5 w-5" /></button>
                    )}
                  </div>
                  {PEDE_BANCO.includes(r.forma) && (
                    <select value={r.conta_id || ''} onChange={(ev) => setRec(i, { conta_id: Number(ev.target.value) || undefined })}
                      className="input h-11 w-full">
                      <option value="">Banco que recebeu o {r.forma}…</option>
                      {(bancos || []).map((b) => <option key={b.id} value={b.id}>{b.nome}{b.banco ? ` · ${b.banco}` : ''}</option>)}
                    </select>
                  )}
                  {r.forma === 'cheque' && (
                    <div className="grid grid-cols-3 gap-2">
                      <input placeholder="Banco" value={r.cheque_banco || ''} onChange={(ev) => setRec(i, { cheque_banco: ev.target.value })} className="input h-10 text-sm" />
                      <input placeholder="Nº cheque" value={r.cheque_numero || ''} onChange={(ev) => setRec(i, { cheque_numero: ev.target.value })} className="input h-10 text-sm" />
                      <input type="date" value={r.cheque_bom_para || ''} onChange={(ev) => setRec(i, { cheque_bom_para: ev.target.value })} className="input h-10 text-sm" />
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => setRecs((rs) => [...rs, { forma: 'dinheiro', valor: 0 }])}
                className="text-sm text-emerald-600 font-semibold">+ outra forma</button>
              <div className="flex justify-between border-t pt-2 font-bold text-ink-800">
                <span>Total recebido</span><span>{formatMoney(totalRec)}</span>
              </div>
            </div>
          ) : (
            <input value={motivo} onChange={(ev) => setMotivo(ev.target.value)}
              placeholder="Motivo (cliente ausente, recusou, endereço…)" className="input h-12 w-full" />
          )}

          {/* Foto / comprovante */}
          <div className="bg-white rounded-2xl border p-3">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFoto} />
            {fotoPreview ? (
              <button onClick={() => fileRef.current?.click()} className="w-full">
                <img src={fotoPreview} className="w-full max-h-56 object-cover rounded-xl" />
                <div className="text-xs text-emerald-600 mt-1 font-semibold">Trocar foto</div>
              </button>
            ) : (
              <button onClick={() => fileRef.current?.click()}
                className="w-full h-28 rounded-xl border-2 border-dashed border-ink-200 flex flex-col items-center justify-center text-ink-400 gap-1">
                <Camera className="h-7 w-7" /><span className="text-sm font-semibold">Foto do canhoto / comprovante</span>
              </button>
            )}
          </div>

          <input value={obs} onChange={(ev) => setObs(ev.target.value)} placeholder="Observação (opcional)" className="input h-11 w-full" />
        </div>

        <div className="sticky bottom-0 bg-white p-4 border-t">
          <button onClick={salvar} disabled={saving}
            className="w-full h-14 rounded-2xl bg-emerald-600 text-white font-extrabold text-lg flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95 transition">
            {saving ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Check className="h-6 w-6" />Confirmar</>}
          </button>
        </div>
      </div>
    </div>
  )
}

// Tela de ACEITE do romaneio: motorista vê rota, dias e totais e aceita (assina) ou recusa.
function RomaneioAceite({ cargaId, recusado, onBack, onAceito }: any) {
  const [enviando, setEnviando] = useState(false)
  const { data: r, isLoading } = useQuery({
    queryKey: ['mot-romaneio', cargaId],
    queryFn: async () => (await motApi.get(`/cargas/${cargaId}/romaneio`)).data,
  })

  async function decidir(aceito: boolean) {
    let observacao: string | null = null
    if (!aceito) {
      observacao = prompt('Por que está recusando esta carga? (opcional)') || null
    } else if (!confirm('Confirma que recebeu a carga e assume a entrega deste romaneio?')) {
      return
    }
    setEnviando(true)
    try {
      const r = await acaoEntregador(`/cargas/${cargaId}/aceitar-romaneio`, { aceito, observacao })
      if (aceito) {
        if (r.online) { toast.success('Romaneio aceito ✓'); onAceito(false) }
        else { toast('Sem internet: aceite salvo no aparelho. Pode rodar.'); onAceito(true) }
      } else {
        toast(r.online ? 'Carga recusada. O escritório foi avisado.' : 'Recusa salva no aparelho.')
        onBack()
      }
    } catch { /* interceptor avisa (erro do servidor) */ } finally { setEnviando(false) }
  }

  if (isLoading || !r) return <div className="min-h-screen grid place-items-center text-ink-400">Carregando romaneio…</div>

  return (
    <div className="min-h-screen bg-ink-50 pb-32">
      <header className="sticky top-0 z-10 bg-emerald-700 text-white px-2 h-14 flex items-center gap-2 shadow">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-white/10"><ChevronLeft className="h-6 w-6" /></button>
        <div className="flex-1">
          <div className="font-bold leading-tight">Romaneio · Carga #{r.numero}</div>
          <div className="text-[11px] text-white/70 leading-tight">Confira e aceite para começar</div>
        </div>
      </header>

      <div className="p-4 max-w-lg mx-auto space-y-4">
        {recusado && (
          <div className="bg-red-50 text-red-700 rounded-2xl p-3 text-sm font-semibold text-center">
            Você recusou esta carga. Pode aceitar agora se quiser assumir.
          </div>
        )}

        {/* Resumo da viagem */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-ink-400 text-xs"><MapPin className="h-4 w-4" />Paradas</div>
            <div className="text-2xl font-extrabold text-ink-800 mt-1">{r.qtd_paradas}</div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-ink-400 text-xs"><CalendarDays className="h-4 w-4" />Estimativa</div>
            <div className="text-2xl font-extrabold text-ink-800 mt-1">{r.dias} dia{r.dias > 1 ? 's' : ''}</div>
            <div className="text-[11px] text-ink-400">≈ {r.horas}h de rota</div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-ink-400 text-xs"><Route className="h-4 w-4" />Distância</div>
            <div className="text-xl font-extrabold text-ink-800 mt-1">{r.km > 0 ? `${r.km} km` : '—'}</div>
          </div>
          <div className="bg-white rounded-2xl border p-4">
            <div className="flex items-center gap-2 text-ink-400 text-xs"><Truck className="h-4 w-4" />Veículo</div>
            <div className="text-base font-bold text-ink-800 mt-1 truncate">{r.veiculo || '—'}</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-4 shadow">
          <div className="text-white/80 text-sm">Valor total a entregar</div>
          <div className="text-3xl font-extrabold">{formatMoney(r.valor_total)}</div>
        </div>

        {/* Sequência de entrega */}
        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="px-4 py-2 border-b font-bold text-ink-700 text-sm flex items-center gap-2"><Clock className="h-4 w-4" />Sequência de entrega</div>
          {r.paradas.map((p: any, i: number) => (
            <div key={i} className="px-4 py-3 flex items-center gap-3 border-b last:border-0">
              <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold grid place-items-center text-sm">{p.seq ?? i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-ink-800 truncate">{p.cliente || 'Cliente'}</div>
                <div className="text-xs text-ink-400 truncate">{p.cidade ? `${p.cidade}${p.uf ? '/' + p.uf : ''}` : 'Sem cidade'}</div>
              </div>
              <span className="text-sm font-bold text-ink-700">{formatMoney(p.total)}</span>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-ink-400 text-center">
          Ao aceitar, você confirma o recebimento da carga e assume a entrega deste romaneio (assinatura digital).
        </p>
      </div>

      {/* Ações fixas */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t p-4 grid grid-cols-3 gap-3 max-w-lg mx-auto">
        <button onClick={() => decidir(false)} disabled={enviando}
          className="h-14 rounded-2xl border-2 border-red-200 text-red-600 font-bold flex items-center justify-center gap-1 disabled:opacity-40 active:scale-95">
          <ThumbsDown className="h-5 w-5" />Recusar
        </button>
        <button onClick={() => decidir(true)} disabled={enviando}
          className="col-span-2 h-14 rounded-2xl bg-emerald-600 text-white font-extrabold text-lg flex items-center justify-center gap-2 disabled:opacity-40 active:scale-95">
          {enviando ? <Loader2 className="h-6 w-6 animate-spin" /> : <><ThumbsUp className="h-6 w-6" />Aceitar romaneio</>}
        </button>
      </div>
    </div>
  )
}
